import {
  Button,
  cn,
  Label,
  NativeSelect,
  NativeSelectOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@portal/ui";
import { SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import {
  DEFAULT_MEMO_VIEW,
  MEMO_GROUP_LABELS,
  MEMO_SORT_LABELS,
  type MemoGroup,
  type MemoSort,
  type MemoViewOptions,
} from "./memo-view";

const STORAGE_KEY = "portal:memo-view:v1";
const ALL_OWNERS = "";

/**
 * 並べ替え・グループは、ブラウザに覚えておく(次に開いたときも同じ見え方にする)。
 * 所有者の絞り込みは覚えない(いつのまにか一部しか出ていない、とならないように)。
 * 保存できない環境(プライベートブラウズなど)では既定のまま動く。
 */
export function useMemoViewOptions() {
  const [options, setOptions] = useState<MemoViewOptions>(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "null") as Partial<
        Pick<MemoViewOptions, "sort" | "group">
      > | null;
      return {
        ...DEFAULT_MEMO_VIEW,
        ...(saved?.sort && saved.sort in MEMO_SORT_LABELS ? { sort: saved.sort } : {}),
        ...(saved?.group && saved.group in MEMO_GROUP_LABELS ? { group: saved.group } : {}),
      };
    } catch {
      return DEFAULT_MEMO_VIEW;
    }
  });
  const update = (patch: Partial<MemoViewOptions>) => {
    const next = { ...options, ...patch };
    setOptions(next);
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ sort: next.sort, group: next.group }),
      );
    } catch {
      // 覚えられなくても、今の画面では効く
    }
  };
  return [options, update] as const;
}

/**
 * 一覧の「表示」の設定(ユーザー要望 2026-10-15)。グループ・並び順・所有者での絞り込み。
 * 既定から変えているときは、ボタンに印を付ける。
 */
export function MemoViewOptionsButton({
  options,
  onChange,
  owners,
}: {
  options: MemoViewOptions;
  onChange: (patch: Partial<MemoViewOptions>) => void;
  /** 絞り込みに出す所有者(いま一覧にいる人)。 */
  owners: readonly { email: string; label: string }[];
}) {
  const changed =
    options.sort !== DEFAULT_MEMO_VIEW.sort ||
    options.group !== DEFAULT_MEMO_VIEW.group ||
    options.owner !== null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          aria-label="グループ・並び順・絞り込み"
          title="グループ・並び順・絞り込み"
          className={cn("relative shrink-0", changed && "text-brand")}
        >
          <SlidersHorizontal />
          {changed && (
            <span
              aria-hidden="true"
              className="absolute top-1 right-1 size-1.5 rounded-full bg-brand"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-64 flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="memo-view-group">グループ</Label>
          <NativeSelect
            id="memo-view-group"
            value={options.group}
            onChange={(e) => onChange({ group: e.target.value as MemoGroup })}
          >
            {(Object.keys(MEMO_GROUP_LABELS) as MemoGroup[]).map((g) => (
              <NativeSelectOption key={g} value={g}>
                {MEMO_GROUP_LABELS[g]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="memo-view-sort">並び順</Label>
          <NativeSelect
            id="memo-view-sort"
            value={options.sort}
            onChange={(e) => onChange({ sort: e.target.value as MemoSort })}
          >
            {(Object.keys(MEMO_SORT_LABELS) as MemoSort[]).map((s) => (
              <NativeSelectOption key={s} value={s}>
                {MEMO_SORT_LABELS[s]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="memo-view-owner">所有者</Label>
          <NativeSelect
            id="memo-view-owner"
            value={options.owner ?? ALL_OWNERS}
            onChange={(e) => onChange({ owner: e.target.value || null })}
          >
            <NativeSelectOption value={ALL_OWNERS}>すべての人</NativeSelectOption>
            {owners.map((o) => (
              <NativeSelectOption key={o.email} value={o.email}>
                {o.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        {changed && (
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            onClick={() => onChange(DEFAULT_MEMO_VIEW)}
          >
            元に戻す
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
